import { z } from 'zod';
import { QUANTITY_MAX_DECIMALS } from './enums';
import { ALL_LABEL_OUTCOME_KINDS, type LabelOutcomeKind } from './labelMutability';
import { AppPlatformSchema } from './appConfig';
import {
  LabelIdSchema,
  ProductIdSchema,
  StorageSchema,
  TenantIdSchema,
  UnitOfMeasureSchema,
  UserIdSchema,
} from './schemas';

/**
 * Contagem de estoque do módulo Leitor (`MOD_SCANNER`) — o registro
 * `tenants/{tenantId}/stock_counts/{countId}` e o input do callable
 * `submitStockCount` que o grava.
 *
 * Origem: #983 — 2026-10-16 20:19 +0200 (F4 do épico #464, §7).
 *
 * Por quê: a contagem confere as etiquetas fisicamente presentes contra as
 * vigentes do sistema (lidas · faltando · sobrando) — o que teria evitado o
 * estoque superestimado do #338. O saldo continua função pura das etiquetas
 * (`docs/screens/stock.md`): a contagem **não lança saldo**, só registra o que
 * a pessoa viu, e as correções são baixas/recuperações normais, com rastro.
 *
 * Contrato: o cliente (folha de sessão, Android F4 / iOS F5) monta
 * `SubmitStockCountInput`; o callable valida com este schema, confere
 * membership e o módulo, recalcula `totals` e grava `StockCount`. As rules
 * recusam qualquer escrita do cliente (`firestore.rules`, `stock_counts`).
 *
 * Usado por: callable `submitStockCount` (`apps/functions/src/stock/`);
 * Android `FirebaseStockCountRepository` + `StockCountDto` (espelho manual) e
 * `ScanSession.toSubmitInput`; iOS pelo contrato gerado (`npm run
 * contract:swift`, F5).
 *
 * Arquitetura: datas em epoch-ms como o resto dos documentos
 * (`ReportJob.createdAtMillis`); `finishedAtMillis` é o relógio do SERVIDOR
 * (chave de ordenação da tela Contagens). Os `client*Millis` vêm do
 * `TrustedClock` do aparelho e servem só para a duração exibida — a spec
 * previa `startedAt` como `serverTimestamp`, mas a contagem começa no
 * aparelho, antes de qualquer ida ao servidor (#983).
 *
 * Dados — exemplo anonimizado do documento gravado:
 *   {
 *     "tenantId": "tenant-1", "countId": "sc_3f0c…", "clientCountId": "3f0c…",
 *     "scope": { "kind": "PRODUCT", "productId": "prod-1", "productName": "Frango" },
 *     "title": "Contagem · Frango · 17/10 13:21",              // #989; ausente em doc antigo
 *     "countedByUid": "uid-1", "countedByName": "Ana Silva",   // congelado na hora
 *     "finishedAtMillis": 1760625240500,                       // servidor
 *     "clientStartedAtMillis": 1760624580000, "clientFinishedAtMillis": 1760625240000,
 *     "source": "HID",
 *     "products": [{ "productId": "prod-1", "productName": "Frango", "unit": "KILOGRAM",
 *                    "labelsRead": 3, "quantityRead": 11.5, "labelsExpected": 5, "quantityExpected": 19 }],
 *     "readLabelIds": ["…"], "missingLabelIds": ["…"], "extraLabelIds": ["…"],
 *     "totals": { "read": 3, "missing": 2, "extra": 1 },      // recalculado no servidor
 *     "actions": [{ "kind": "BAIXA", "labelId": "…", "outcome": "DISCARDED", "baixaId": "…" },
 *                 { "kind": "RECOVER", "labelId": "…" }],
 *     "appVersion": "2026.10.16+281", "platform": "android"
 *   }
 *
 * Falhas conhecidas:
 *  - 2026-10-16 (#983, auditoria): `productName` da linha exigia `min(1)` e a
 *    etiqueta antiga sem nome derrubava a contagem inteira; os totais contavam
 *    `length` (id repetido inflava). Ver os comentários no ponto de cada um.
 *
 * Dicas: mexeu aqui → `npm run contract && npm run contract:swift` no mesmo
 * commit (gate `contract:check`). Os tetos (`STOCK_COUNT_MAX_*`) mantêm o doc
 * longe de 1 MiB: 5 000 ids de 20 caracteres ≈ 120 KB.
 *
 * Issues: #464 (épico), #983 (F4), #338 (estoque superestimado), #323 (parcial),
 * #989 (título editável).
 *
 * Histórico:
 *  - 2026-10-17 13:21 +0200 — (#989) `title` opcional, editável ao salvar.
 */

/** Teto de ids somados (lidas + faltando + sobrando) num registro. */
export const STOCK_COUNT_MAX_LABEL_IDS = 5000;
/** Teto de linhas de produto num registro. */
export const STOCK_COUNT_MAX_PRODUCTS = 500;
/** Teto de ações (baixas/recuperações) registradas. */
export const STOCK_COUNT_MAX_ACTIONS = 5000;

/** O que entra no escopo esperado da contagem. */
export const StockCountScopeKind = {
  ALL: 'ALL',
  PRODUCT: 'PRODUCT',
  STORAGE: 'STORAGE',
} as const;
export type StockCountScopeKind = (typeof StockCountScopeKind)[keyof typeof StockCountScopeKind];
export const ALL_STOCK_COUNT_SCOPE_KINDS = Object.values(StockCountScopeKind) as [
  StockCountScopeKind,
  ...StockCountScopeKind[],
];
export const StockCountScopeKindSchema = z.enum(ALL_STOCK_COUNT_SCOPE_KINDS);

/** De onde vieram as leituras. `MIXED` = leitor HID e câmera na mesma contagem. */
export const StockCountSource = {
  HID: 'HID',
  CAMERA: 'CAMERA',
  MIXED: 'MIXED',
} as const;
export type StockCountSource = (typeof StockCountSource)[keyof typeof StockCountSource];
export const ALL_STOCK_COUNT_SOURCES = Object.values(StockCountSource) as [
  StockCountSource,
  ...StockCountSource[],
];
export const StockCountSourceSchema = z.enum(ALL_STOCK_COUNT_SOURCES);

/** Correção feita na folha antes de registrar. */
export const StockCountActionKind = {
  BAIXA: 'BAIXA',
  RECOVER: 'RECOVER',
} as const;
export type StockCountActionKind = (typeof StockCountActionKind)[keyof typeof StockCountActionKind];
export const ALL_STOCK_COUNT_ACTION_KINDS = Object.values(StockCountActionKind) as [
  StockCountActionKind,
  ...StockCountActionKind[],
];
export const StockCountActionKindSchema = z.enum(ALL_STOCK_COUNT_ACTION_KINDS);

type Brand<K, T> = K & { readonly __brand: T };
export type StockCountId = Brand<string, 'StockCountId'>;
export const StockCountIdSchema = z
  .string()
  .min(1)
  .transform((s) => s as StockCountId)
  .meta({ type: 'string', minLength: 1 });

/** Teto do título da contagem (#989) — cabe numa linha da lista no celular. */
export const STOCK_COUNT_TITLE_MAX = 80;

/** Prefixo do id do documento: `sc_<clientCountId>` (idempotência no callable). */
export const STOCK_COUNT_ID_PREFIX = 'sc_';
export function stockCountIdFor(clientCountId: string): StockCountId {
  return `${STOCK_COUNT_ID_PREFIX}${clientCountId}` as StockCountId;
}

/** Quantidade somada: ≥ 0, finita, no máximo `QUANTITY_MAX_DECIMALS` casas. */
const SummedQuantitySchema = z
  .number()
  .nonnegative()
  .refine((q) => Number.isFinite(q) && Number(q.toFixed(QUANTITY_MAX_DECIMALS)) === q, {
    message: `quantidade com mais de ${String(QUANTITY_MAX_DECIMALS)} casas decimais`,
  });

/**
 * Escopo congelado ao "Começar a contar". `productName` vai junto para a tela
 * Contagens exibir o escopo mesmo depois de o produto ser apagado.
 */
export const StockCountScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(StockCountScopeKind.ALL) }),
  z.object({
    kind: z.literal(StockCountScopeKind.PRODUCT),
    productId: ProductIdSchema,
    productName: z.string().min(1).max(200),
  }),
  z.object({ kind: z.literal(StockCountScopeKind.STORAGE), storage: StorageSchema }),
]);
export type StockCountScope = z.infer<typeof StockCountScopeSchema>;

/**
 * Lidas × esperadas de um produto, na unidade dele. `unit`/`quantity*` são
 * `null` quando as etiquetas do produto não carregam quantidade: a linha
 * mostra só o número de etiquetas.
 */
export const StockCountProductSchema = z.object({
  productId: ProductIdSchema,
  // Auditoria 2026-10-16 21:25 +0200 (#983): era `.min(1).max(200)`, mas o nome
  // vem da etiqueta (`LabelSchema.productName` é `z.string()` sem mínimo nem
  // teto) — uma etiqueta antiga sem nome no escopo fazia o servidor recusar a
  // contagem inteira, e todo reenvio falhava igual. Vazio é aceito; o teto
  // segue (o cliente corta em 200, `FirebaseStockCountRepository`).
  productName: z.string().max(200),
  unit: UnitOfMeasureSchema.nullable(),
  labelsRead: z.number().int().nonnegative(),
  labelsExpected: z.number().int().nonnegative(),
  quantityRead: SummedQuantitySchema.nullable(),
  quantityExpected: SummedQuantitySchema.nullable(),
});
export type StockCountProduct = z.infer<typeof StockCountProductSchema>;

/**
 * Correção que a folha já gravou pelas rotas normais (`labels` + ledger) antes
 * de registrar. O registro não grava baixa nenhuma: só a referencia.
 * `baixaId` é `null` na rota legada e em `EXPIRED` (transição de status, sem
 * ledger — `LabelBaixaPatch`).
 */
export const StockCountActionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(StockCountActionKind.BAIXA),
    labelId: LabelIdSchema,
    outcome: z.enum(ALL_LABEL_OUTCOME_KINDS as [LabelOutcomeKind, ...LabelOutcomeKind[]]),
    baixaId: z.string().min(1).nullable(),
  }),
  z.object({ kind: z.literal(StockCountActionKind.RECOVER), labelId: LabelIdSchema }),
]);
export type StockCountAction = z.infer<typeof StockCountActionSchema>;

const LabelIdListSchema = z.array(LabelIdSchema);

const stockCountBody = {
  tenantId: TenantIdSchema,
  /** uuid gerado ao "Começar a contar"; sobrevive à morte do processo. */
  clientCountId: z.string().regex(/^[0-9a-f-]{8,64}$/, 'clientCountId inválido'),
  scope: StockCountScopeSchema,
  /**
   * Nome que a pessoa deu ao salvar (#989): a folha propõe um padrão
   * ("Contagem · <escopo> · dd/MM HH:mm") e deixa editar. Opcional porque os
   * documentos gravados antes da #989, e os apps sem a folha nova, não o têm —
   * quem lê cai num rótulo derivado do escopo e da data.
   */
  title: z.string().trim().min(1).max(STOCK_COUNT_TITLE_MAX).optional(),
  source: StockCountSourceSchema,
  products: z.array(StockCountProductSchema).max(STOCK_COUNT_MAX_PRODUCTS),
  readLabelIds: LabelIdListSchema,
  missingLabelIds: LabelIdListSchema,
  extraLabelIds: LabelIdListSchema,
  actions: z.array(StockCountActionSchema).max(STOCK_COUNT_MAX_ACTIONS),
  clientStartedAtMillis: z.number().int().nonnegative(),
  clientFinishedAtMillis: z.number().int().nonnegative(),
  appVersion: z.string().min(1).max(64),
  platform: AppPlatformSchema,
};

/** Tetos e coerência que dependem de mais de um campo. */
function refineBody(
  v: {
    readLabelIds: readonly string[];
    missingLabelIds: readonly string[];
    extraLabelIds: readonly string[];
    clientStartedAtMillis: number;
    clientFinishedAtMillis: number;
  },
  ctx: z.RefinementCtx,
): void {
  const ids = v.readLabelIds.length + v.missingLabelIds.length + v.extraLabelIds.length;
  if (ids > STOCK_COUNT_MAX_LABEL_IDS) {
    ctx.addIssue({
      code: 'custom',
      message: `mais de ${String(STOCK_COUNT_MAX_LABEL_IDS)} ids de etiqueta`,
      path: ['readLabelIds'],
    });
  }
  if (v.clientFinishedAtMillis < v.clientStartedAtMillis) {
    ctx.addIssue({
      code: 'custom',
      message: 'clientFinishedAtMillis anterior a clientStartedAtMillis',
      path: ['clientFinishedAtMillis'],
    });
  }
}

/** Input do callable `submitStockCount` (ver o cabeçalho do arquivo). */
export const SubmitStockCountInputSchema = z.object(stockCountBody).superRefine(refineBody);
export type SubmitStockCountInput = z.infer<typeof SubmitStockCountInputSchema>;

/** Saída do callable: o id gravado e se a chamada repetiu uma anterior. */
export const SubmitStockCountOutputSchema = z.object({
  countId: StockCountIdSchema,
  deduplicated: z.boolean(),
});
export type SubmitStockCountOutput = z.infer<typeof SubmitStockCountOutputSchema>;

/** Contadores do resumo, recalculados pelo servidor a partir das listas. */
export const StockCountTotalsSchema = z.object({
  read: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
  extra: z.number().int().nonnegative(),
});
export type StockCountTotals = z.infer<typeof StockCountTotalsSchema>;

/**
 * O documento `tenants/{tid}/stock_counts/{countId}`: o input mais o que o
 * servidor acrescenta. Escrito só por `submitStockCount`; imutável.
 */
export const StockCountSchema = z
  .object({
    ...stockCountBody,
    countId: StockCountIdSchema,
    countedByUid: UserIdSchema,
    countedByName: z.string(),
    totals: StockCountTotalsSchema,
    finishedAtMillis: z.number().int().nonnegative(),
  })
  .superRefine(refineBody);
export type StockCount = z.infer<typeof StockCountSchema>;

/**
 * Totais a partir das listas — o servidor nunca confia nos do cliente.
 *
 * Auditoria 2026-10-16 21:31 +0200 (#983): contava `length`, e um id repetido
 * na lista (bug de cliente) inflava o total; agora conta ids distintos. Não
 * recusa o payload: perder a contagem inteira por uma duplicata seria pior.
 */
export function stockCountTotals(input: {
  readLabelIds: readonly unknown[];
  missingLabelIds: readonly unknown[];
  extraLabelIds: readonly unknown[];
}): StockCountTotals {
  return {
    read: new Set(input.readLabelIds).size,
    missing: new Set(input.missingLabelIds).size,
    extra: new Set(input.extraLabelIds).size,
  };
}
